import React from 'react';
import { Utils as QbUtils } from '@react-awesome-query-builder/ui';
import { DIR_FILTER_BASIC_FIELDS } from '../../../../advanced_filters/fields/basic_filter_fields';
import { Popover } from '../../../../../design_system/components/ToolTip/ToolTip.jsx';
import ConditionalRender from '../../../../../ConditionalRender';

// Operators whose saved value is a single multi-value array (value: [[a, b, c]]).
const EXPANDABLE_MULTISELECT_OPERATORS = ['multiselect_equals', 'multiselect_not_equals'];

const MULTISELECT_BUILDERS = ['DirectoryDropdownBuilder', 'CheckboxTypeBuilder'];
const isExpandableField = (field) =>
  MULTISELECT_BUILDERS.includes(DIR_FILTER_BASIC_FIELDS?.[field]?.renderBuilder);

const childrenAsArray = (kids) =>
  Array.isArray(kids) ? kids : Object.values(kids || {});

const getMultiSelectTerms = (value) => {
  if (Array.isArray(value?.[0])) return value[0];
  if (Array.isArray(value) && value.every((v) => v != null && typeof v !== 'object')) {
    return value;
  }
  return null;
};

export const expandMultiSelectRules = (node) => {
  if (!node || typeof node !== 'object') return node;

  if (node.type === 'rule') {
    const { field, operator, value } = node.properties || {};
    if (!EXPANDABLE_MULTISELECT_OPERATORS.includes(operator) || !isExpandableField(field)) {
      return node;
    }
    const terms = getMultiSelectTerms(value);
    if (!terms || terms.length === 0) return node;

    if (terms.length === 1) {
      // Single value: normalise flat -> nested so the widget renders it and sanitize is happy.
      return {
        ...node,
        properties: {
          ...node.properties,
          value: [[terms[0]]],
          valueSrc: node.properties.valueSrc || ['value'],
          valueType: node.properties.valueType || ['multiselect'],
        },
      };
    }

    return {
      id: QbUtils.uuid(),
      type: 'group',
      properties: {
        conjunction: operator === 'multiselect_equals' ? 'OR' : 'AND',
        not: false,
      },
      children1: terms.map((term) => ({
        id: QbUtils.uuid(),
        type: 'rule',
        properties: {
          field: node.properties.field,
          operator: node.properties.operator,
          value: [[term]],
          valueSrc: node.properties.valueSrc || ['value'],
          valueType: node.properties.valueType || ['multiselect'],
        },
      })),
    };
  }

  if (node.type === 'group' || node.type === 'switch_group' || node.type === 'rule_group') {
    if (!node.children1) return node;
    return {
      ...node,
      children1: childrenAsArray(node.children1).map((child) => expandMultiSelectRules(child)),
    };
  }

  return node;
};

export const mergeBaseAndAdhocTree = (adhocTree, config, baseFilterTree) => {
  const adhocJson = QbUtils.getTree(QbUtils.sanitizeTree(adhocTree, config).fixedTree);
  if (!baseFilterTree) return adhocJson;
  const isPlainAnd = (node) =>
    node?.type === 'group' && (node.properties?.conjunction || 'AND') === 'AND' && !node.properties?.not;
  const asChildren = (node) => (isPlainAnd(node) ? childrenAsArray(node.children1) : [node]);
  return {
    id: QbUtils.uuid(),
    type: 'group',
    properties: { conjunction: 'AND', not: false },
    children1: [...asChildren(baseFilterTree), ...asChildren(adhocJson)],
  };
};

// Control-width helpers — shared across builder value controls.
// Depth-based: ValueDropdown's generic container narrows as filter nesting deepens.
export const getValueControlWidthByDepth = (depth) => {
  if (depth <= 2) return 300;
  if (depth === 3) return 230;
  return 180;
};

// Fixed single-value control max-width — used by Boolean/Checkbox SelectBox wrappers.
export const SINGLE_VALUE_CONTROL_MAX_WIDTH = 250;

export const FIELD_CATEGORIES = {
  Person: [
    "Name", "Headline", "Current Title", "Location",
    "City", "State", "Country/Region", "Years of Experience",
  ],
  "Company Attributes": [
    "Company Size", "Company Type", "Company Industry", "Company Location", "Company Revenue",
  ],
  Company: [
    "Current Company", "Industry", "Current Start Date",
    "Seniority Level", "Years in Current Company",
    "Years in Current Role", "Past Companies",
  ],
  Education: [
    "Education", "Degree", "Major",
    "Education Start Date", "Education End Date", "All Educations",
  ],
  "Additional Fields": [
    "Volunteering Roles", "Volunteering Types", "Skills",
  ],
};

export const capitalizeFirstLetter = (val) => {
  if (val == null) return "";
  const str = String(val);
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const getFieldLabel = (field, config) => {
  return config.fields?.[field]?.label || field;
}

export const getBooleanValueLabel = (field, operator, config) => {
  const fieldDef = config.fields?.[field];
  if (fieldDef?.type !== 'boolean') return null;
  const listValues = fieldDef.fieldSettings?.listValues || [];
  if (operator === 'is_not_null') return listValues.find(i => i.value === true)?.label || 'Matched';
  if (operator === 'is_null') return listValues.find(i => i.value === false)?.label || 'Unmatched';
  return null;
};

export const getOperatorLabel = (field, operator, config) => {
  const fieldDef = config.fields?.[field];
  if (fieldDef?.type === 'boolean') return "is";
  if (fieldDef?.operatorLabels && fieldDef.operatorLabels[operator]) {
    return fieldDef.operatorLabels[operator].short || fieldDef.operatorLabels[operator].long || operator;
  }
  else if (config?.operators) {
    const operatorDef = config.operators[operator];
    return operatorDef?.label || operator;
  }
  return config.operators?.[operator]?.label || operator;
}

export const getValueLabel = (field, value, config) => {
  const fieldDef = config.fields?.[field];
  if (!fieldDef?.fieldSettings?.listValues) return value;

  if (Array.isArray(value)) {
    return value.map(v => {
      const item = fieldDef.fieldSettings.listValues.find(i => i.value === v);
      return item?.title || item?.label || v;
    });
  }

  const item = fieldDef.fieldSettings.listValues.find(i => i.value === value);
  return item?.title || item?.label || value;
};

export const extractTreeDetails = (node, config) => {
  const result = {
    rootGroupId: null,
    groups: {},
    rules: {}
  };

  const walk = (currentNode, parentGroupId = null) => {
    if (currentNode.type === 'group') {
      const groupId = currentNode.id;

      if (!result.rootGroupId) {
        result.rootGroupId = groupId;
      }

      result.groups[groupId] = {
        id: groupId,
        conjunction: currentNode.properties?.conjunction || 'AND',
        parentGroupId,
        children: []
      };

      Object.values(currentNode.children1 || {}).forEach(child => {
        result.groups[groupId].children.push(child.id);
        walk(child, groupId);
      });
    }

    if (currentNode.type === 'rule') {
      const field = currentNode.properties.field;
      const operator = currentNode.properties.operator;
      const rawValue = Array.isArray(currentNode.properties.value)
        ? currentNode.properties.value.flat(Infinity)
        : currentNode.properties.value;

      result.rules[currentNode.id] = {
        id: currentNode.id,
        field,
        fieldLabel: getFieldLabel(field, config),
        operator,
        operatorLabel: getOperatorLabel(field, operator, config),
        value: {
          value: rawValue,
          label: getValueLabel(field, rawValue, config)
        }
      };
    }
  };

  walk(node);
  return result;
};

export const getTreeDepth = (groupId, groups) => {
  const group = groups[groupId];
  if (!group || !group.children || group.children.length === 0) {
    return 1;
  }

  return (
    1 +
    Math.max(
      ...group.children.map(childId =>
        getTreeDepth(childId, groups)
      )
    )
  );
};

export const renderMultiColumnValues = (data_values) => {
  const values = data_values?.filter(item => item?.label) || [];
  if (!values.length) return null;

  const [firstValue, ...otherValues] = values;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'nowrap'
        }}
      >
        <div className="body-m-semibold color-text-secondary">
          {firstValue.label}
        </div>

        <ConditionalRender if={otherValues.length > 0}>
          <Popover
            tooltipText={() => (
              <div style={{ maxHeight: 100, overflowY: 'scroll' }}>
                {otherValues.map((value, idx) => (
                  <div key={idx}>{value.label}</div>
                ))}
              </div>
            )}
            placement="bottom-start"
          >
            <div
              className="text_link"
              style={{ whiteSpace: 'nowrap' }}
            >
              +{otherValues.length} more
            </div>
          </Popover>
        </ConditionalRender>
      </div>
    </div>
  );
};

export const validateFilterTree = (node, path = []) => {
  const invalidPaths = [];

  const children = node.children1 || [];

  children.forEach((child) => {
    const childPath = [...path, child.id];

    if (child.type === "rule") {
      const { field, operator, value } = child.properties || {};

      const isException =
        operator === "has_no_value" ||
        field === "prospect_id";

      let isValid = true;

      if (!field || !operator) {
        isValid = false;
      } else if (!isException) {
        if (!Array.isArray(value) || value.length === 0) {
          isValid = false;
        }
      }

      if (!isValid) {
        invalidPaths.push(childPath);
      }
    } else {
      const result = validateFilterTree(child, childPath);
      invalidPaths.push(...result.invalidPaths);
    }
  });

  return {
    isValid: invalidPaths.length === 0,
    invalidPaths,
  };
};

export const countRulesInTree = (node) => {
  let count = 0;

  const children = Array.isArray(node?.children1)
    ? node.children1
    : Object.values(node?.children1 || {});

  children.forEach((child) => {
    if (child.type === "rule") {
      count += 1;
    } else {
      count += countRulesInTree(child);
    }
  });

  return count;
};

export const normalizeValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .flat(Infinity)
      .filter((item) => item !== undefined && item !== null && item !== "" && item !== "__dummy__");
  }

  if (value === undefined || value === null || value === "" || value === "__dummy__") {
    return [];
  }

  return [value];
};

export const highlightMatch = (text, query, MINCHAR = 2, highlightStyle = { color: 'var(--color-action-primary)' }) => {
  if (!query || query.length < MINCHAR) return text;

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) =>
      regex.test(part) ? (
          <span key={index} style={highlightStyle}>{part}</span>
      ) : (
          part
      )
  );
};
